// src/gestion-logistics/components/SupplierSelector.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Select, Spin } from "antd";
import { ShopOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../../config/api";
import { deduplicateEntities } from "../../utils/entityUtils";

const { Option } = Select;

interface Supplier {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  company_id?: number;
  user_type?: string;
}

interface SupplierSelectorProps {
  value?: number;
  onChange?: (value: number, supplierName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  companyId?: number | null; // Filtrer par entreprise depuis RightSideNav
  warehouseId?: number | null; // Filtrer par magasin depuis RightSideNav
  allowedSupplierIds?: number[]; // Filtrer par liste blanche (ex: fournisseurs du dossier)
}

/**
 * Composant de sélection de fournisseur avec autocomplete
 * Charge les fournisseurs depuis /api/users/suppliers (table users avec user_type='suppliers')
 */
const SupplierSelector: React.FC<SupplierSelectorProps> = ({
  value,
  onChange,
  placeholder = "Rechercher un fournisseur...",
  disabled = false,
  companyId,
  warehouseId,
  allowedSupplierIds,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      // Construire les paramètres de requête pour filtrage multi-tenant
      const params: any = {};
      if (companyId) params.companyId = companyId;
      if (warehouseId) params.warehouseId = warehouseId;

      const response = await axios.get(`${API_CONFIG.API_URL}/users/suppliers`, { params });

      if (Array.isArray(response.data)) {
        // Dédoublonner les fournisseurs (évite les doublons de jointures SQL)
        const uniqueSuppliers = deduplicateEntities(response.data);

        const supplierData = uniqueSuppliers.map((item: any) => ({
          id: item.id,
          name: item.name,
          email: item.email,
          phone: item.phone,
          company_id: item.company_id,
          user_type: item.user_type,
        }));

        setSuppliers(supplierData);
      }
    } catch (error) {
      console.error("[LOGISTICS] Error fetching suppliers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les fournisseurs quand companyId ou warehouseId change
  useEffect(() => {
    if (companyId) {
      fetchSuppliers();
    } else {
      setSuppliers([]);
    }
  }, [companyId, warehouseId]);

  const filteredSuppliers = useMemo(() => {
    if (!allowedSupplierIds) {
      return suppliers;
    }
    if (allowedSupplierIds.length === 0) {
      return [];
    }
    const allowedSet = new Set(allowedSupplierIds);
    return suppliers.filter((supplier) => allowedSet.has(supplier.id));
  }, [allowedSupplierIds, suppliers]);

  const handleChange = (selectedId: number) => {
    const selectedSupplier = filteredSuppliers.find((s) => s.id === selectedId);
    if (selectedSupplier && onChange) {
      onChange(selectedId, selectedSupplier.name);
    }
  };

  const filterOption = (input: string, option: any) => {
    const label = option?.children?.props?.children?.[0]?.props?.children || "";
    const email = option?.children?.props?.children?.[1]?.props?.children || "";
    const searchTerm = input.toLowerCase();
    return (
      label.toString().toLowerCase().includes(searchTerm) ||
      email.toString().toLowerCase().includes(searchTerm)
    );
  };

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      onChange={handleChange}
      filterOption={filterOption}
      loading={loading}
      disabled={disabled}
      notFoundContent={loading ? <Spin size="small" /> : "Aucun fournisseur trouvé"}
      style={{ width: "100%" }}
      suffixIcon={<ShopOutlined />}
      allowClear
    >
      {filteredSuppliers.map((supplier) => (
        <Option key={supplier.id} value={supplier.id}>
          <div>
            <div style={{ fontWeight: "bold" }}>{supplier.name}</div>
            {supplier.email && (
              <div style={{ fontSize: "12px", color: "#999" }}>
                {supplier.email}
              </div>
            )}
          </div>
        </Option>
      ))}
    </Select>
  );
};

export default SupplierSelector;
